// Task #3041 — Hook preferenze avatar personale del PA (per-utente).
//
// GET/PUT via apiClient su `/personal-assistant/avatar-prefs` (rotta
// tenant-agnostica: tenant dal JWT, utente dall'identità autenticata).
// DTO condivisi da @easyflow/shared (gate check-response-dto-shared).
// Il salvataggio emette un evento window così ogni superficie che monta
// l'hook (FAB, workspace, dialog) resta coerente senza reload.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import type { PaAvatarPrefs, PaAvatarPrefsUpdate } from '@easyflow/shared';

export const PA_AVATAR_PREFS_UPDATED_EVENT = 'pa-avatar-prefs-updated';

const DEFAULT_PREFS: PaAvatarPrefs = { enabled: false, assistantName: null, avatar: null };

export function usePaAvatarPrefs(): {
  prefs: PaAvatarPrefs;
  loaded: boolean;
  save: (update: PaAvatarPrefsUpdate) => Promise<PaAvatarPrefs>;
} {
  const [prefs, setPrefs] = useState<PaAvatarPrefs>(DEFAULT_PREFS);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<PaAvatarPrefs>('/personal-assistant/avatar-prefs');
      if (res.success && res.data) setPrefs(res.data);
    } catch {
      // Fallback esplicito: prefs di default = icona attuale (stato OFF).
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const handler = () => { void load(); };
    window.addEventListener(PA_AVATAR_PREFS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(PA_AVATAR_PREFS_UPDATED_EVENT, handler);
  }, [load]);

  const save = useCallback(async (update: PaAvatarPrefsUpdate): Promise<PaAvatarPrefs> => {
    const res = await apiClient.put<PaAvatarPrefs>('/personal-assistant/avatar-prefs', update);
    if (!res.success || !res.data) {
      throw new Error(res.error || 'Salvataggio avatar non riuscito');
    }
    setPrefs(res.data);
    window.dispatchEvent(new Event(PA_AVATAR_PREFS_UPDATED_EVENT));
    return res.data;
  }, []);

  return { prefs, loaded, save };
}
